import { baseTabWithRates, hasStagedPricing } from 'pages/pricelists/modal/matterPricelistEditorUtils';
import { useCallback, useState } from 'react';
import { UseFormGetValues, UseFormSetValue } from 'react-hook-form';
import { MatterForm, PricelistReference, PricelistTabDraft } from 'types/matters';
import { MatterFollowedPricelist } from 'types/pricelists';
import { t } from 'utils/getTranslations';

interface KeptPricing {
    pricelists: PricelistTabDraft[];
    referencedPricelist: PricelistReference | null;
    followsPricelist: MatterFollowedPricelist | null;
}

/**
 * What a currency change does to the form's staged lists. The save wipes every list of the case on a
 * currency change, so the form drops to one empty base tab and keeps the old lists aside; switching back
 * to the stored currency before the save restores them exactly, staged edits included, and any other
 * currency gives an empty base tab again. {@code storedCurrency} is the one the kept lists are priced in:
 * the case's on an edit, the source's on a copy, none on a blank form.
 */
export const useCurrencyChangePricing = (
    storedCurrency: string | null,
    getValues: UseFormGetValues<MatterForm>,
    setValue: UseFormSetValue<MatterForm>
) => {
    const [keptPricing, setKeptPricing] = useState<KeptPricing | null>(null);

    const applyCurrencyChange = useCallback(
        (currency: string | null) => {
            if (currency === storedCurrency) {
                if (keptPricing) {
                    setValue('pricelists', keptPricing.pricelists, { shouldDirty: true });
                    setValue('referencedPricelist', keptPricing.referencedPricelist, { shouldDirty: true });
                    setValue('followsPricelist', keptPricing.followsPricelist, { shouldDirty: true });
                    setKeptPricing(null);
                }
                return;
            }
            const [pricelists, referencedPricelist, followsPricelist] = getValues([
                'pricelists',
                'referencedPricelist',
                'followsPricelist',
            ]);
            if (!keptPricing && storedCurrency) {
                setKeptPricing({ pricelists, referencedPricelist, followsPricelist: followsPricelist ?? null });
            }
            // A followed list is in the old currency too, so the case stops following it.
            setValue('pricelists', [baseTabWithRates()], { shouldDirty: true });
            setValue('referencedPricelist', null, { shouldDirty: true });
            setValue('followsPricelist', null, { shouldDirty: true });
        },
        [getValues, setValue, storedCurrency, keptPricing]
    );

    // The notice on the price field while lists priced in the stored currency wait to be removed by the save.
    const currencyChangeNotice = (currency: string | null | undefined): string | null =>
        keptPricing &&
        storedCurrency &&
        currency !== storedCurrency &&
        (hasStagedPricing(keptPricing.pricelists) || keptPricing.followsPricelist !== null)
            ? t('matters.pricing-currency-change-notice', { currency: storedCurrency })
            : null;

    // A follower's currency change ends the following and leaves one empty list, which the save takes as it is.
    const endsFollowing = (currency: string | null | undefined): boolean =>
        !!keptPricing?.followsPricelist && !!storedCurrency && currency !== storedCurrency;

    return { applyCurrencyChange, currencyChangeNotice, endsFollowing };
};
